import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { isExpired } from '@/lib/timezone';

interface PurchaseStatus {
  hasPurchased: boolean;
  isLoading: boolean;
  productType: string | null;
  remainingGenerations: number;
  isAdmin: boolean;
}

export const usePurchaseStatus = (productType: 'mascot' | 'brand_kit') => {
  const [status, setStatus] = useState<PurchaseStatus>({
    hasPurchased: false,
    isLoading: true,
    productType: null,
    remainingGenerations: 6,
    isAdmin: false,
  });

  useEffect(() => {
    checkPurchaseStatus();
  }, [productType]);

  const checkPurchaseStatus = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setStatus({
          hasPurchased: false,
          isLoading: false,
          productType: null,
          remainingGenerations: 0,
          isAdmin: false,
        });
        return;
      }

      // Check if user has admin role from database (secure server-side check)
      const { data: roleData } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .eq('role', 'admin')
        .maybeSingle();

      const isAdmin = !!roleData;

      // Admin bypass - always has access
      if (isAdmin) {
        setStatus({
          hasPurchased: true,
          isLoading: false,
          productType: productType,
          remainingGenerations: 999, // Admin has unlimited generations for testing
          isAdmin: true,
        });
        return;
      }

      const { data, error } = await supabase
        .from('purchases')
        .select('*, expires_at')
        .eq('user_id', user.id)
        .eq('product_type', productType)
        .maybeSingle();

      if (!error && data) {
        // Check if purchase has expired using direct UTC comparison
        const hasExpired = isExpired(data.expires_at);
        
        if (hasExpired) {
          console.log('Purchase expired:', data.expires_at);
          setStatus({
            hasPurchased: false,
            isLoading: false,
            productType: null,
            remainingGenerations: 0,
            isAdmin: false,
          });
        } else {
          setStatus({
            hasPurchased: true,
            isLoading: false,
            productType: data.product_type,
            remainingGenerations: 6,
            isAdmin: false,
          });
        }
      } else {
        setStatus({
          hasPurchased: false,
          isLoading: false,
          productType: null,
          remainingGenerations: 0,
          isAdmin: false,
        });
      }
    } catch (error) {
      console.error('Error checking purchase:', error);
      setStatus({
        hasPurchased: false,
        isLoading: false,
        productType: null,
        remainingGenerations: 0,
        isAdmin: false,
      });
    }
  };

  const refetchStatus = () => {
    checkPurchaseStatus();
  };

  return { ...status, refetchStatus };
};
